import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import facilityStyles from "../styles.module.scss";
import styles from "./styles.module.scss";
import MoreIcon from "../../../../public/images/more.svg";
import EditIcon from "../../../../public/images/edit-black.svg";
import DeleteIcon from "../../../../public/images/delete-red.svg";
import PagePanel from "app/components/PagePanel";
import { Loading } from "app/components/Wrappers";
import { cls } from "app/utils";
import AddNewMenu from "./AddNewMenu";
import ReorderList from "./ReorderList";
import ResourceCard from "./ResourceCard";
import ResourceModal from "./ResourceModal";
import SectionModal, { SectionModalMode, SectionValues } from "./SectionModal";
import useCloseOnOutsideClick from "./useCloseOnOutsideClick";
import { SectionGroup, groupSections, orderPayload } from "./resourceOrder";
import {
  ScheduleResourceResponse,
  ScheduleResourcePayload,
  ResourceCategoryResponse,
  ResourceOrderPayload,
} from "app/utils/request/scheduling/facilityResources";

interface ResourcesViewProps {
  isLoading: boolean;
  // Coaches and schedulers read the page. It has no header actions, no menus
  // and no reorder mode.
  readOnly?: boolean;
  resources: ScheduleResourceResponse[];
  categories: ResourceCategoryResponse[];
  // Both report whether the write succeeded. A failure keeps the modal open.
  onAdd: (payload: ScheduleResourcePayload) => Promise<boolean>;
  onEdit: (
    resourceId: number,
    payload: ScheduleResourcePayload
  ) => Promise<boolean>;
  onToggleActive: (resource: ScheduleResourceResponse) => Promise<void>;
  onDelete: (resourceId: number) => Promise<void>;
  // Rejects with the API errors, so the modal can show them.
  onSaveSection: (values: SectionValues) => Promise<void>;
  onDeleteSection: (categoryId: number) => void;
  // Reports whether the save succeeded. A failure keeps the reorder mode open.
  onSaveOrder: (order: ResourceOrderPayload) => Promise<boolean>;
}

interface SectionMenuProps {
  category: ResourceCategoryResponse;
  onRename: (category: ResourceCategoryResponse) => void;
  onDelete: (category: ResourceCategoryResponse) => void;
}

/**
 * The "..." menu of a section, with Rename and Delete (decision Q9). The
 * trigger takes the place and the look of the section pencil of Figma
 * 68:7019: a grey 32px circle before the title.
 */
const SectionMenu: React.FC<SectionMenuProps> = ({
  category,
  onRename,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useCloseOnOutsideClick(menuRef, open, () => setOpen(false));

  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <div className={styles.menu_anchor} ref={menuRef}>
      <button
        type="button"
        className={styles.section_more}
        onClick={() => setOpen(!open)}
        aria-label={t("Section actions")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <img src={MoreIcon} alt="" width={20} height={20} />
      </button>
      {open && (
        <div
          className={cls(styles.action_menu, styles.action_menu_start)}
          role="menu"
          data-testid="section-menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(() => onRename(category))}
            className={styles.menu_item}
          >
            <img src={EditIcon} alt="" width={24} height={24} />
            {t("Rename")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(() => onDelete(category))}
            className={cls(styles.menu_item, styles.menu_item_danger)}
          >
            <img src={DeleteIcon} alt="" width={24} height={24} />
            {t("Delete")}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * The Resources panel: the header with Reorder and "Add New", the sections
 * with their cards, the reorder mode, and the two modals. The read-only panel
 * shows the sections and the cards only.
 */
const ResourcesView: React.FC<ResourcesViewProps> = ({
  isLoading,
  readOnly = false,
  resources,
  categories,
  onAdd,
  onEdit,
  onToggleActive,
  onDelete,
  onSaveSection,
  onDeleteSection,
  onSaveOrder,
}) => {
  const { t } = useTranslation();

  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleResourceResponse | null>(null);
  const [sectionModalMode, setSectionModalMode] =
    useState<SectionModalMode | null>(null);
  const [renaming, setRenaming] = useState<ResourceCategoryResponse | null>(
    null
  );

  // Reorder state, as on the Booking Options page.
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderGroups, setReorderGroups] = useState<SectionGroup[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // A section is a category record, so an empty section stays after a reload.
  const sections = groupSections(categories, resources);

  const openAddResource = () => {
    setEditing(null);
    setIsResourceModalOpen(true);
  };

  const openEdit = (resource: ScheduleResourceResponse) => {
    setEditing(resource);
    setIsResourceModalOpen(true);
  };

  const closeResourceModal = () => {
    setIsResourceModalOpen(false);
    setEditing(null);
  };

  const openCreateSection = () => {
    setRenaming(null);
    setSectionModalMode("create");
  };

  const openRenameSection = (category: ResourceCategoryResponse) => {
    setRenaming(category);
    setSectionModalMode("rename");
  };

  const handleDeleteSection = (category: ResourceCategoryResponse) => {
    onDeleteSection(category.id);
  };

  const closeSectionModal = () => {
    setSectionModalMode(null);
    setRenaming(null);
  };

  const handleSaveSection = async (values: SectionValues) => {
    await onSaveSection(values);
    closeSectionModal();
  };

  const handleSave = async (payload: ScheduleResourcePayload) => {
    const saved = editing
      ? await onEdit(editing.id, payload)
      : await onAdd(payload);

    if (saved) {
      closeResourceModal();
    }
  };

  const handleEnterReorderMode = () => {
    setReorderGroups(sections);
    setIsReorderMode(true);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderGroups([]);
  };

  const handleSaveOrder = async () => {
    if (isSavingOrder) {
      return;
    }
    setIsSavingOrder(true);
    const saved = await onSaveOrder(orderPayload(reorderGroups));
    setIsSavingOrder(false);
    if (saved) {
      setIsReorderMode(false);
      setReorderGroups([]);
    }
  };

  // Save comes first and Cancel next to it, as on the Booking Options page
  // (decision Q11). All header buttons are the pills of Figma 83:10365.
  const header = isReorderMode ? (
    <div className={cls(styles.header_actions, styles.header_actions_reorder)}>
      <button
        type="button"
        className={cls(styles.pill_button, styles.pill_primary)}
        disabled={isSavingOrder}
        onClick={handleSaveOrder}
      >
        {t("Save")}
      </button>
      <button
        type="button"
        className={cls(styles.pill_button, styles.pill_secondary)}
        onClick={handleCancelReorder}
      >
        {t("Cancel")}
      </button>
    </div>
  ) : (
    <div className={styles.header_actions}>
      {sections.length > 0 && (
        <button
          type="button"
          className={cls(styles.pill_button, styles.pill_secondary)}
          onClick={handleEnterReorderMode}
        >
          {t("Reorder")}
        </button>
      )}
      <AddNewMenu
        onAddResource={openAddResource}
        onAddSection={openCreateSection}
      />
    </div>
  );

  const renderList = () => (
    <div className={styles.section_list}>
      {sections.length === 0 && (
        <div className={styles.empty_state}>
          <p>
            {readOnly
              ? t("No sections yet.")
              : t(
                  "No sections yet. Add a section, then add your bays or rooms to it."
                )}
          </p>
        </div>
      )}

      {sections.map(({ category, resources: sectionResources }) => (
        <div key={category.id} className={styles.section_block}>
          <div className={styles.section_head}>
            {!readOnly && (
              <SectionMenu
                category={category}
                onRename={openRenameSection}
                onDelete={handleDeleteSection}
              />
            )}
            <h3>{category.title}</h3>
          </div>

          {sectionResources.length > 0 ? (
            <div className={styles.card_grid}>
              {sectionResources.map((resource) =>
                readOnly ? (
                  <ResourceCard key={resource.id} resource={resource} />
                ) : (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    onEdit={openEdit}
                    onToggleActive={onToggleActive}
                    onDelete={onDelete}
                  />
                )
              )}
            </div>
          ) : (
            <div className={styles.empty_section}>
              {t("No resource in this section yet.")}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderBody = () => {
    if (isLoading) {
      return <Loading isLoading loadType="spinner" />;
    }

    return isReorderMode ? (
      <ReorderList groups={reorderGroups} onChange={setReorderGroups} />
    ) : (
      renderList()
    );
  };

  return (
    <PagePanel
      className={styles.resources_page}
      panelClassName={styles.resources_panel}
      title={t("Resources")}
      titleStyles={facilityStyles.header}
      headerChildren={readOnly ? undefined : header}
    >
      {renderBody()}

      <ResourceModal
        isOpen={isResourceModalOpen}
        resource={editing}
        categories={sections.map(({ category }) => category)}
        resources={resources}
        onClose={closeResourceModal}
        onSave={handleSave}
      />

      <SectionModal
        isOpen={sectionModalMode !== null}
        mode={sectionModalMode ?? "create"}
        category={renaming}
        existingTitles={categories.map((category) => category.title)}
        onClose={closeSectionModal}
        onSave={handleSaveSection}
      />
    </PagePanel>
  );
};

export default ResourcesView;
